/**
 * 离线降级 Hook — 检测网络状态，自动降级
 * 对齐 features.md 9.21 + engineering.md §二错误处理
 *
 * 9.21 离线重试队列：
 * - 离线时写请求失败 → BffClient 存入 localStorage
 * - 恢复在线 → 自动重放队列
 * - OfflineBanner 显示「N 个操作待重试」
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getPendingRequests,
  replayPendingRequests,
} from '../api/bff-client';

export function useOnlineStatus(): {
  isOnline: boolean;
  wasOffline: boolean;
  /** 当前待重试的操作数 */
  pendingCount: number;
} {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);
  const [pendingCount, setPendingCount] = useState(() => getPendingRequests().length);

  const handleOnline = useCallback(async () => {
    setIsOnline(true);
    // 恢复在线 → 重放队列
    try {
      await replayPendingRequests();
    } catch {
      // 重放过程中的错误已被内部捕获，这里兜底
    }
    setPendingCount(getPendingRequests().length);
  }, []);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setWasOffline(true);
    setPendingCount(getPendingRequests().length);
  }, []);

  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return { isOnline, wasOffline, pendingCount };
}

/**
 * 离线提示横幅 — 网络断开时显示
 * 9.21：展示待重试的操作数
 */
export function OfflineBanner() {
  const { isOnline, pendingCount } = useOnlineStatus();

  if (isOnline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-30 bg-error text-white text-center text-sm py-1.5 px-4">
      📴 网络已断开 — 阅读和离线词典可用，翻译和 AI 对话需要网络
      {pendingCount > 0 && (
        <span className="ml-2 font-medium">{pendingCount} 个操作待重试</span>
      )}
    </div>
  );
}
